import { useEffect, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useLazyGetExtendedProfileQuery } from "../../../../../redux/api/user/userAPI";
import { selectProfile } from "../../../../../redux/features/user/user-slice";
import { UserRole } from "../../../enums/user-roles";
import { AppRoute } from "../../../interfaces/app-routes";
import IExtendedUserProfile, {
  IExtendedUserProfileData,
} from "../../../interfaces/extendedUserProfile";
import { IHttpError } from "../../../interfaces/http-errror";
import BackArrowButton from "../../../ui-elements/back-arrow-button/back-arrow-button";
import SpinnerModal from "../../../ui-elements/spinner/spinner";
import { showErrorMessage } from "../../../utils/toast";
import UserProfileDataEdit from "../components/user-profile-data-edit/user-profile-data-edit";
import UserProfileData from "../components/user-profile-data/user-profile-data";
import styles from "./profile-details.module.scss";

const defaultProfile: IExtendedUserProfileData = {
  id: "",
  firstName: "",
  lastName: "",
  username: "",
  nic: "",
  email: "",
  isEmailVerified: false,
  contactNumber: "",
  isContactNumberVerified: false,
  spaceClusters: [],
  serviceProviderShortName: "",
  privileges: [],
};

const ProfileDetails = () => {
  const navigate = useNavigate();
  const profile = useSelector(selectProfile);
  const [userProfileData, setUserProfileData] =
    useState<IExtendedUserProfileData>({ ...defaultProfile });

  const [triggerGetExtendedProfile, { isFetching: extendProfileDataFetching }] =
    useLazyGetExtendedProfileQuery();

  const getExtendedProfile = () => {
    triggerGetExtendedProfile()
      .unwrap()
      .then((response: IExtendedUserProfile) => {
        setUserProfileData(response.userProfileView);
      })
      .catch((error: IHttpError) => {
        showErrorMessage(error.message || "Something went wrong");
      });
  };

  useEffect(() => {
    getExtendedProfile();
  }, []);

  const showEditIcon =
    profile &&
    (profile.role === UserRole.ROLE_USER ||
      profile.role === UserRole.ROLE_ADMIN);

  // Get initials for avatar
  const getInitials = () => {
    const first = userProfileData.firstName?.charAt(0) || "";
    const last = userProfileData.lastName?.charAt(0) || "";
    const initials = `${first}${last}`.toUpperCase();
    return initials || "US";
  };

  return (
    <div className="w-100 position-relative">
      <BackArrowButton onBack={() => navigate(-1)} />
      <div className={`${styles.avatarContainer} mt-3`}>
        <div className={styles.avatar}>{getInitials()}</div>
        <div className={styles.avatarName}>
          <p className={styles.firstName}>
            {userProfileData.firstName || "User"}
          </p>
          <p className={styles.lastName}>{userProfileData.lastName || ""}</p>
        </div>
      </div>
      <Row>
        <Col className="col-12 col-lg-8 col-xl-6 col-xxl-5">
          <Row>
            <Col>
              <UserProfileData
                firstName={userProfileData.firstName}
                lastName={userProfileData.lastName}
                username={userProfileData.username}
                getExtendedProfile={getExtendedProfile}
                showEditIcon={!!showEditIcon}
              />
            </Col>
          </Row>
          <Row className="mt-3">
            <Col>
              <UserProfileDataEdit
                fields={[{ label: "Email", value: userProfileData.email }]}
                showEditIcon={!!showEditIcon}
                handleEdit={() => navigate(AppRoute.CHANGE_EMAIL)}
              />
            </Col>
          </Row>
          <Row className="mt-3">
            <Col>
              <UserProfileDataEdit
                fields={[{ label: "Password", value: "• • • • • • •" }]}
                showEditIcon={!!showEditIcon}
                handleEdit={() =>
                  navigate(AppRoute.CHANGE_PASSWORD, {
                    state: {
                      isNavigateToProfile: false,
                    },
                  })
                }
              />
            </Col>
          </Row>
        </Col>
      </Row>
      <SpinnerModal show={extendProfileDataFetching} />
    </div>
  );
};

export default ProfileDetails;
